// context/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔵 AuthProvider: Initializing...');
    
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('🔵 AuthProvider: Getting initial session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ AuthProvider: Error getting session:', error);
        } else {
          console.log('✅ AuthProvider: Initial session:', session ? 'Found' : 'None');
          setSession(session);
          setUser(session?.user || null);
        }
      } catch (error) {
        console.error('❌ AuthProvider: Error in getInitialSession:', error);
      } finally {
        setLoading(false);
        console.log('🔵 AuthProvider: Initial session check complete');
      }
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔔 Auth state changed:', event, session?.user?.email);
        
        setSession(session);
        setUser(session?.user || null);
        setLoading(false);

        // Handle different events if needed
        if (event === 'SIGNED_OUT') {
          console.log('👋 User signed out');
        }
        if (event === 'SIGNED_IN') {
          console.log('👤 User signed in:', session?.user?.email);
        }
        if (event === 'TOKEN_REFRESHED') {
          console.log('🔄 Token refreshed');
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      console.log('🔵 AuthProvider: Cleaning up subscription');
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    email = email.trim();
    console.log('🔐 Login attempt for:', email);
    
    if (!isValidEmail(email)) {
      console.log('❌ Login: Invalid email format');
      return { success: false, message: 'Invalid email format' };
    }

    try {
      console.log('🔐 Login: Calling signInWithPassword...');
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (error) {
        console.error('❌ Login error:', {
          message: error.message,
          status: error.status,
          name: error.name
        });
        
        // Provide better error message for unverified emails
        if (error.message.includes('Email not confirmed')) {
          console.log('📧 Login: Email not confirmed');
          return { 
            success: false, 
            message: 'Please verify your email before logging in. Check your inbox for the verification link.' 
          };
        }
        
        if (error.message.includes('Invalid login credentials')) {
          console.log('❌ Login: Invalid credentials');
          return { 
            success: false, 
            message: 'Invalid email or password. If you just registered, please verify your email first.' 
          };
        }
        
        return { success: false, message: error.message };
      }

      console.log('✅ Login successful:', {
        user: data.user?.email,
        hasSession: !!data.session
      });
      
      return { success: true, message: 'Logged in successfully' };
    } catch (error) {
      console.error('❌ Unexpected login error:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const register = async (name, email, password) => {
    email = email.trim();
    console.log('📝 Register attempt:', { name, email });
    
    if (!isValidEmail(email)) {
      console.log('❌ Register: Invalid email format');
      return { success: false, message: 'Invalid email format' };
    }

    try {
      // Determine the redirect URL based on environment
      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      
      const redirectUrl = isDevelopment 
        ? 'http://localhost:3000/dashboard'
        : 'https://allsportshubs.com/dashboard';

      console.log('📝 Register: Environment config:', {
        isDevelopment,
        redirectUrl,
        hostname: window.location.hostname,
        NODE_ENV: process.env.NODE_ENV
      });

      console.log('📝 Register: Calling signUp with options:', {
        email,
        redirectUrl,
        hasPassword: !!password,
        metadata: { full_name: name }
      });

      const { data, error } = await supabase.auth.signUp({
        email, 
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { 
            full_name: name 
          }
        }
      });

      console.log('📝 Register: Response received:', {
        hasData: !!data,
        hasError: !!error,
        hasUser: !!data?.user,
        hasSession: !!data?.session,
        userId: data?.user?.id,
        userEmail: data?.user?.email,
        emailConfirmed: data?.user?.email_confirmed_at
      });

      if (error) {
        console.error('❌ Register error:', {
          message: error.message,
          status: error.status,
          name: error.name,
          fullError: error
        });
        
        // Handle specific error cases
        if (error.message.includes('already registered')) {
          console.log('📧 Register: Email already registered');
          return { 
            success: false, 
            message: 'This email is already registered. Please login instead.' 
          };
        }
        
        return { success: false, message: error.message };
      }

      // Check if email confirmation is required
      if (data.user && !data.session) {
        console.log('📧 Email confirmation required for:', email);
        console.log('📧 User created with ID:', data.user.id);
        console.log('📧 Email confirmation status:', data.user.email_confirmed_at);
        console.log('📧 User should check email at:', email);
        
        return { 
          success: true, 
          message: 'Registration successful! Please check your email to verify your account before logging in.',
          requiresVerification: true
        };
      } else if (data.session) {
        console.log('✅ User registered and logged in immediately:', email);
        console.log('✅ Session created:', !!data.session);
        
        return { 
          success: true, 
          message: 'Registration successful! You are now logged in.',
          requiresVerification: false
        };
      }

      console.log('⚠️ Unexpected registration state:', { data });
      return { 
        success: true, 
        message: 'Registration successful! Please check your email.' 
      };
    } catch (error) {
      console.error('❌ Unexpected registration error:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const logout = async () => {
    try {
      console.log('👋 Logout attempt...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Logout error:', error);
      } else {
        console.log('✅ Logout successful');
      }
    } catch (error) {
      console.error('❌ Unexpected logout error:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log('🔵 Google sign-in attempt...');
      
      if (typeof window === 'undefined') {
        console.log('❌ Not in browser environment');
        return { success: false, message: 'Not in browser environment' };
      }

      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.port === '3000';
      
      let redirectUrl;
      
      if (isDevelopment) {
        redirectUrl = 'http://localhost:3000/dashboard';
      } else {
        redirectUrl = 'https://allsportshubs.com/dashboard';
      }
      
      console.log('🔵 Google OAuth config:', {
        environment: isDevelopment ? 'development' : 'production',
        redirectUrl
      });
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });

      if (error) {
        console.error('❌ Google sign-in error:', error);
        return { success: false, message: error.message };
      }

      console.log('✅ Google OAuth initiated:', data);
      return { success: true, message: 'Redirecting to Google...' };
    } catch (error) {
      console.error('❌ Unexpected Google OAuth error:', error);
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const value = {
    user,
    session,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    signInWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;